import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Button Group component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 408:3986):
 * - Sizes: sm (24px), md (32px), lg (36px)
 * - Connected buttons with shared border
 * - First item active (white bg), others have 5% black bg
 * - Dividers between items
 */
const buttonGroupVariants = cva(
  'inline-flex items-center overflow-hidden bg-white shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.07),0px_1px_3px_0px_rgba(18,18,18,0.1)] dark:bg-grey-900 dark:shadow-[0px_1px_1px_0px_rgba(255,255,255,0.05),0px_0px_0px_1px_rgba(255,255,255,0.1)]',
  {
    variants: {
      size: {
        sm: 'rounded-md',
        md: 'rounded-lg',
        lg: 'rounded-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const buttonGroupItemVariants = cva(
  'inline-flex items-center gap-2 px-3 text-sm font-medium text-grey-950 transition-colors hover:bg-black/5 focus:outline-none focus:bg-black/10 dark:text-grey-50 dark:hover:bg-white/5 dark:focus:bg-white/10',
  {
    variants: {
      size: {
        sm: 'h-6',
        md: 'h-8',
        lg: 'h-9',
      },
      active: {
        true: 'bg-white dark:bg-grey-900',
        false: 'bg-black/5 dark:bg-white/5',
      },
    },
    defaultVariants: {
      size: 'md',
      active: false,
    },
  }
);

const ButtonGroupContext = React.createContext({ size: 'md' });

const ButtonGroup = React.forwardRef(
  ({ className, size = 'md', children, ...props }, ref) => {
    const items = React.Children.toArray(children);

    return (
      <ButtonGroupContext.Provider value={{ size }}>
        <div
          ref={ref}
          className={cn(buttonGroupVariants({ size }), className)}
          role="group"
          {...props}
        >
          {items.map((child, index) => (
            <React.Fragment key={index}>
              {index > 0 && (
                <div className="h-full w-px bg-black/10 dark:bg-white/10" />
              )}
              {child}
            </React.Fragment>
          ))}
        </div>
      </ButtonGroupContext.Provider>
    );
  }
);
ButtonGroup.displayName = 'ButtonGroup';

const ButtonGroupItem = React.forwardRef(
  ({ className, active = false, leftIcon, rightIcon, children, ...props }, ref) => {
    const { size } = React.useContext(ButtonGroupContext);

    return (
      <button
        ref={ref}
        type="button"
        className={cn(buttonGroupItemVariants({ size, active }), className)}
        data-active={active}
        {...props}
      >
        {leftIcon && <span className="shrink-0">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);
ButtonGroupItem.displayName = 'ButtonGroupItem';

export { ButtonGroup, ButtonGroupItem, buttonGroupVariants, buttonGroupItemVariants };
